import React, { createContext, useContext, useState, useEffect } from 'react';

const CampaignContext = createContext();

export const useCampaigns = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaigns must be used within a CampaignProvider');
  }
  return context;
};

export const CampaignProvider = ({ children }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load campaigns from localStorage on mount
  useEffect(() => {
    const savedCampaigns = localStorage.getItem('prosper-crm-campaigns');
    if (savedCampaigns) {
      setCampaigns(JSON.parse(savedCampaigns));
    }
  }, []);

  // Save campaigns to localStorage whenever campaigns change
  useEffect(() => {
    localStorage.setItem('prosper-crm-campaigns', JSON.stringify(campaigns));
  }, [campaigns]);

  const createCampaign = (campaignData) => {
    const newCampaign = {
      id: Date.now().toString(),
      ...campaignData,
      createdAt: new Date().toISOString(),
      status: 'draft',
      stats: {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        replied: 0,
        bounced: 0
      },
      recipients: campaignData?.recipients ?? []
    };
    
    setCampaigns(prev => [newCampaign, ...prev]);
    return newCampaign;
  };

  const updateCampaign = (id, updates) => {
    setCampaigns(prev => 
      prev.map(campaign => 
        campaign.id === id 
          ? { ...campaign, ...updates, updatedAt: new Date().toISOString() }
          : campaign
      )
    );
  };

  const deleteCampaign = (id) => {
    setCampaigns(prev => prev.filter(campaign => campaign.id !== id));
  };

  const launchCampaign = async (id) => {
    setLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateCampaign(id, {
        status: 'active',
        launchedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Failed to launch campaign:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const pauseCampaign = (id) => {
    updateCampaign(id, {
      status: 'paused',
      pausedAt: new Date().toISOString()
    });
  };

  const getCampaignStats = () => {
    const stats = campaigns.reduce((acc, campaign) => {
      acc.total += 1;
      acc.sent += campaign.stats.sent;
      acc.delivered += campaign.stats.delivered;
      acc.opened += campaign.stats.opened;
      acc.clicked += campaign.stats.clicked;
      
      if (campaign.status === 'active') acc.active += 1;
      if (campaign.status === 'completed') acc.completed += 1;
      if (campaign.status === 'draft') acc.draft += 1;
      
      return acc;
    }, {
      total: 0,
      active: 0,
      completed: 0,
      draft: 0,
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0
    });

    return stats;
  };

  const getRecentCampaigns = (limit = 5) => {
    return campaigns
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, limit);
  };

  const value = {
    campaigns,
    activeCampaign,
    setActiveCampaign,
    loading,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    launchCampaign,
    pauseCampaign,
    getCampaignStats,
    getRecentCampaigns
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};